import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import { Box, Grid, Link, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AddOnItemName, TemplateIntakeForm } from "schemas/dashboard";
import { routes } from "schemas/routes";

import { PlanAction } from "features/Home/UpgradeDialog/Plan/helpers";
import { fetcherAuth } from "utils/api";
import { useSubscription } from "utils/useSubscription";

import CardWrapper from "../components/CardWrapper";
import styles from "./styles";

const BENEFITS = [
  {
    icon: "💯",
    primary: "Increase response rate",
    secondary:
      "We will highlight your background and unique selling points to make you stand out",
  },
  {
    icon: "⏰",
    primary: "Fast turn-around time",
    secondary: "Receive your template within 5 business days",
  },
];

const AddOnTemplate = () => {
  const { addOns } = useContext(SubscriptionContext);
  const { onPlanButtonClick, checkoutLoading } = useSubscription();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);

  const [templateIntakeForms, setTemplateIntakeForms] = useState<
    TemplateIntakeForm[]
  >([]);
  const navigate = useNavigate();

  const fetchIntakeForms = async () => {
    try {
      const { templateIntakeForms } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/template-intake-forms`,
      );
      setTemplateIntakeForms(templateIntakeForms || []);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  useEffect(() => {
    if (currentOrg) {
      fetchIntakeForms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return (
    <CardWrapper>
      <Grid item>
        <Typography variant="h5" component="div" gutterBottom>
          Custom Pitch Template
        </Typography>

        <Typography variant="h6" sx={{ color: "green.main" }} gutterBottom>
          ${addOns?.[AddOnItemName.pitching_templates]?.price.toFixed(2)}
          /template
          <Box component="span" sx={styles.annotationPrice}>
            One-time fee
          </Box>
        </Typography>

        <Typography gutterBottom>
          Make every word count! A professional copywriter will polish your
          pitch so brands can't wait to work with you.
        </Typography>

        {BENEFITS.map((benefit, index) => (
          <Grid container key={index} alignItems="center" sx={{ mb: 3 }}>
            <Grid
              container
              justifyContent="left"
              alignItems="center"
              item
              xs="auto"
            >
              <Box sx={styles.iconBlock}>{benefit.icon}</Box>
            </Grid>
            <Grid item xs md>
              <Typography>
                <strong>{benefit.primary}</strong>
              </Typography>
              <Typography>{benefit.secondary}</Typography>
            </Grid>
          </Grid>
        ))}
      </Grid>

      {templateIntakeForms?.length > 0 && (
        <Grid sx={{ mb: 2 }}>
          <Typography gutterBottom>
            You currently have <strong>{templateIntakeForms?.length}</strong>{" "}
            pending intake form(s).
          </Typography>
          {templateIntakeForms.map((form, index) => (
            <Typography component="div" key={index}>
              <Link
                sx={{
                  cursor: "pointer",
                  color: "purple.main",
                  textDecoration: "underline",
                  textDecorationColor: "purple.main",
                }}
                onClick={() => {
                  navigate(`/${routes.intakeForms}/${form.addOnItemId}`);
                }}
                target="_blank"
              >
                Intake Form {index + 1}
              </Link>
            </Typography>
          ))}
        </Grid>
      )}

      <Grid item>
        <LoadingButton
          variant="contained"
          disableElevation
          loading={checkoutLoading === AddOnItemName.pitching_templates}
          onClick={() =>
            onPlanButtonClick(
              AddOnItemName.pitching_templates,
              PlanAction.upgrade,
            )
          }
        >
          Purchase
        </LoadingButton>

        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          <Box
            component="i"
            className="fa-regular fa-circle-info"
            sx={{ mr: 1 }}
          />
          Holiday hours: Template purchases between Dec 24-Jan 3 will be
          delivered by Jan 9
        </Typography>
      </Grid>
    </CardWrapper>
  );
};

export default AddOnTemplate;
